import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FormInput,
  PrimarySecondaryButtonContainer,
  SubmitButton,
} from '@/components/FormComponents';
import { FormErrorAlert } from '@/components/FormErrorAlert';
import { FeatureFlagsEditor } from '@/components/FeatureFlagsEditor';
import { Card } from '@/components/ui/card';
import { useAdvisorSearch } from '../hooks/use-advisor-search';
import { useUpdateFeatureFlags } from '../hooks/use-update-feature-flags';
import { getErrorMessage } from '../utils/get-error-message';

const schema = z.object({
  advisorQuery: z.string().min(1, 'Advisor ID or Email is required'),
});

export function FeatureFlagsForm() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQueryRef = useRef(searchParams.get('advisorId') ?? '');
  const [featureFlags, setFeatureFlags] = useState<string>('{}');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { advisorQuery: initialQueryRef.current },
  });

  const { searchAdvisor, advisor, isPending: isSearching, isError: isSearchError, error: searchError } =
    useAdvisorSearch();
  const { updateFeatureFlags, isPending: isUpdating, isError: isUpdateError, error: updateError } =
    useUpdateFeatureFlags();

  useEffect(() => {
    if (initialQueryRef.current) {
      searchAdvisor(initialQueryRef.current);
    }
  }, [searchAdvisor]);

  useEffect(() => {
    if (advisor?.featureFlags) {
      setFeatureFlags(JSON.stringify(advisor.featureFlags));
    } else if (advisor) {
      setFeatureFlags('{}');
    }
  }, [advisor]);

  const onSearchAdvisor = (data: z.infer<typeof schema>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('advisorId', data.advisorQuery);
      return next;
    }, { replace: true });
    searchAdvisor(data.advisorQuery);
  };

  const onUpdateFeatureFlags = () => {
    if (!advisor) return;
    updateFeatureFlags({ advisorId: advisor.advisorId, featureFlags });
  };

  return (
    <div className="space-y-6">
      <form
        onSubmit={handleSubmit(onSearchAdvisor)}
        className="space-y-4"
      >
        <FormInput
          label="Advisor ID or Email"
          id="advisorQuery"
          type="text"
          placeholder="Enter advisor ID or email address"
          error={errors.advisorQuery}
          register={register('advisorQuery')}
          required={true}
        />

        <PrimarySecondaryButtonContainer>
          <SubmitButton
            label="Search Advisor"
            isLoading={isSearching}
          />
        </PrimarySecondaryButtonContainer>

        {isSearchError && (
          <FormErrorAlert
            mainErrorMessage={getErrorMessage(searchError, 'Advisor not found')}
            className="mt-4"
          />
        )}
      </form>

      {advisor && (
        <Card className="space-y-4 p-6">
          <div className="border-b pb-4">
            <h3 className="text-lg font-medium">Advisor Information</h3>
            <div className="mt-2 text-sm text-gray-700">
              <p>
                <strong>ID:</strong> {advisor.advisorId}
              </p>
              <p>
                <strong>Name:</strong> {advisor.advisorFirstName}{' '}
                {advisor.advisorLastName}
              </p>
              <p>
                <strong>Email:</strong> {advisor.advisorEmail}
              </p>
              <p>
                <strong>Organization:</strong>{' '}
                {advisor.organizationDisplayName ||
                  advisor.organizationName ||
                  'N/A'}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-base font-medium">Feature Flags</h4>
            <FeatureFlagsEditor
              value={featureFlags}
              onChange={setFeatureFlags}
              error={false}
              warnOnNylEnrollment
            />

            <PrimarySecondaryButtonContainer>
              <SubmitButton
                label="Update Feature Flags"
                isLoading={isUpdating}
                onClick={onUpdateFeatureFlags}
              />
            </PrimarySecondaryButtonContainer>

            {isUpdateError && (
              <FormErrorAlert
                mainErrorMessage={getErrorMessage(updateError, 'Failed to update feature flags')}
                isWarning={true}
                className="mt-4"
              />
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
